import React, { useRef, useState, useEffect } from "react";
import { ArrowBackIos } from "@mui/icons-material";
import { useSelector } from "react-redux";
import {
  useGetJobDetails,
  useGetJobTypes,
  useGetSalesPerson,
  useUpdateJob,
} from "../apiCalls/jobApiCalls";
import { useParams } from "react-router-dom";
import Loader from "../components/Loader";
import { useGetUserDetails } from "../apiCalls/userApiCalls";
import moment from "moment";

export default function EditJob() {
  const titleInputElement = useRef();
  const amountInputElement = useRef();
  const noteInputElement = useRef();
  const endDateInputElement = useRef();
  const collectionDateInputElement = useRef();

  const [foremanShare, setForemanShare] = useState(100);
  const [workerShares, setWorkerShares] = useState({});
  const [selectedTypeId, setSelectedTypeId] = useState("");
  const [selectedSalesId, setSelectedSalesId] = useState("");
  const [selectedCollected, setSelectedCollected] = useState("false");

  const { currentUser } = useSelector((state) => state.userSlice) || null;
  const token = currentUser.token;
  const userId = currentUser.user._id;
  const { jobId } = useParams();

  const { isLoading: isJobDetailsLoading, data: jobDetails } = useGetJobDetails(
    jobId,
    token
  );

  const { isLoading: isUserLoading, data: userDetails } = useGetUserDetails(
    userId,
    token
  );

  const { data: salesPersons } = useGetSalesPerson(token);
  const { data: jobTypes } = useGetJobTypes(token);

  const { mutate: updateJobMutate, isLoading: isUpdateJobLoading } =
    useUpdateJob();

  const handleSubmit = (event) => {
    event.preventDefault();

    const inputEndDate = moment(endDateInputElement.current?.value);
    const adjustedEndDate = inputEndDate.set({
      hour: 12,
      minute: 0,
      second: 0,
    });
    const utcEndDate = adjustedEndDate.utc().toDate();

    const inputCollectionDate = moment(
      collectionDateInputElement.current?.value
    );
    const adjustedCollectionDate = inputCollectionDate.set({
      hour: 12,
      minute: 0,
      second: 0,
    });
    const utcCollectionDate = adjustedCollectionDate.utc().toDate();

    const data = {
      jobId: jobId,
      token: token,
      title: titleInputElement.current?.value,
      amount: amountInputElement.current?.value,
      salesId: selectedSalesId,
      typeId: selectedTypeId,
      note: noteInputElement.current?.value,
      endDate: utcEndDate,
      collectionDate: utcCollectionDate,
      collected: selectedCollected,
      foremanShare,
      split: Object.entries(workerShares).map(([workerId, share]) => ({
        worker: workerId,
        share,
      })),
    };

    updateJobMutate(data);
  };

  const handleWorkerShareChange = (workerId, value) => {
    const newWorkerShares = { ...workerShares };
    newWorkerShares[workerId] = Math.min(Math.max(0, value), 100);
    setWorkerShares(newWorkerShares);

    // Calculate total worker share
    const totalWorkerShare = Object.values(newWorkerShares).reduce(
      (sum, share) => sum + share,
      0
    );
    // Update foreman share to maintain 100% total
    setForemanShare(Math.max(0, 100 - totalWorkerShare));
  };

  useEffect(() => {
    // Initialize worker shares from job details if they exist
    if (jobDetails?.data?.data?.split) {
      const initialShares = {};
      jobDetails.data.data.split.forEach((split) => {
        // worker is an ObjectId reference
        initialShares[split.worker] = split.share;
      });
      setWorkerShares(initialShares);
      
      // Calculate total worker share and set foreman share accordingly
      const totalWorkerShare = Object.values(initialShares).reduce(
        (sum, share) => sum + share,
        0
      );
      setForemanShare(Math.max(0, 100 - totalWorkerShare));
    } else if (userDetails?.data?.data?.workers) {
      // Initialize worker shares to 0 when user details are loaded
      const initialShares = {};
      userDetails.data.data.workers.forEach((worker) => {
        initialShares[worker._id] = 0;
      });
      setWorkerShares(initialShares);
      // When no split exists, foreman gets 100% by default
      setForemanShare(100);
    }
    // Set selectedTypeId, selectedSalesId, and selectedCollected when jobDetails loads
    if (jobDetails?.data?.data?.typeId) {
      setSelectedTypeId(jobDetails.data.data.typeId);
    }
    if (jobDetails?.data?.data?.salesId?._id) {
      setSelectedSalesId(jobDetails.data.data.salesId._id);
    }
    if (jobDetails?.data?.data) {
      setSelectedCollected(String(jobDetails.data.data.collected ?? false));
    }
  }, [jobDetails, userDetails]);

  return (
    <>
      {isJobDetailsLoading ? (
        <Loader />
      ) : (
        <>
          <div className="flex w-full items-center justify-center">
            <div className="mx-6 md:mx-32 lg:mx-44 xl:mx-64 mt-32 w-full lg:w-2/5">
              <div className="fixed lg:left-20 xl:left-32 mt-2 hidden lg:flex lg:items-center lg:justify-center items-center justify-center w-10 h-10 rounded-full bg-white shadow btn-shadow hover:shadow-s, hover:bg-stone-50 transition-colors">
                <button
                  onClick={() => window.history.back()}
                  className="w-full h-full rounded-full flex items-center justify-center"
                >
                  <ArrowBackIos
                    style={{ fontSize: 24, paddingLeft: 6, color: "gray" }}
                  />
                </button>
              </div>
              <form onSubmit={handleSubmit}>
                <div className="pb-8 pt-10">
                  <div className="space-y-3">
                    <div className="space-y-2">
                      <label
                        className="text-base font-medium text-gray-500"
                        htmlFor=""
                      >
                        Title of Job
                      </label>
                      <div className="rounded-xl py-4 px-5 flex gap-2 border border-black">
                        <input
                          type="text"
                          className="outline-none w-full text-base font-medium text-black placeholder-black"
                          name="title"
                          defaultValue={jobDetails.data.data.title}
                          ref={titleInputElement}
                        />
                      </div>
                    </div>

                    <div className="space-y-2">
                      <label
                        className="text-base font-medium text-gray-500"
                        htmlFor=""
                      >
                        Job Type
                      </label>
                      <div className="rounded-xl w-full py-4 px-5 flex gap-2 border border-black">
                        <select
                          name="workerIds"
                          className="w-full outline-none"
                          value={selectedTypeId}
                          onChange={(e) => setSelectedTypeId(e.target.value)}
                        >
                          {jobTypes?.data.data.map((type) => (
                            <option key={type._id} value={type._id}>
                              {type.type === "Percentage"
                                ? `${type.title} @ ${type.rate}%`
                                : `${type.title} @ $${type.rate} per ${type.type}`}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>

                    <div className="space-y-2">
                      <label
                        className="text-base font-medium text-gray-500"
                        htmlFor=""
                      >
                        Items/Amount/Area of Job
                      </label>
                      <div className="rounded-xl py-4 px-5 flex gap-2 border border-black">
                        <input
                          type="number"
                          className="outline-none w-full text-base font-medium text-black placeholder-black"
                          name="amount"
                          defaultValue={jobDetails.data.data.amount}
                          ref={amountInputElement}
                          step=".01"
                          onWheel={(e) => e.target.blur()}
                        />
                      </div>
                    </div>

                    <div className="space-y-2">
                      <label
                        className="text-base font-medium text-gray-500"
                        htmlFor=""
                      >
                        Sales Person
                      </label>
                      <div className="rounded-xl w-full py-4 px-5 flex gap-2 border border-black">
                        <select
                          name="workerIds"
                          className="w-full outline-none"
                          value={selectedSalesId}
                          onChange={(e) => setSelectedSalesId(e.target.value)}
                        >
                          {salesPersons?.data.data.map((worker) => (
                            <option key={worker._id} value={worker._id}>
                              {worker.name}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>

                    <div className="space-y-2">
                      <label
                        className="text-base font-medium text-gray-500"
                        htmlFor=""
                      >
                        Note
                      </label>
                      <div className="rounded-xl py-4 px-5 flex gap-2 border border-black">
                        <input
                          type="text"
                          className="outline-none w-full text-base font-medium text-black placeholder-black"
                          name="note"
                          defaultValue={jobDetails.data.data.note}
                          ref={noteInputElement}
                        />
                      </div>
                    </div>

                    <div className="space-y-2">
                      <label
                        className="text-base font-medium text-gray-500"
                        htmlFor=""
                      >
                        End date
                      </label>
                      <div className="rounded-xl py-4 px-5 flex gap-2 border border-black">
                        <input
                          type="date"
                          className="w-full outline-none bg-white"
                          name="endDate"
                          defaultValue={
                            jobDetails.data.data.endDate
                              ? new Date(jobDetails.data.data.endDate)
                                  .toISOString()
                                  .split("T")[0]
                              : ""
                          }
                          ref={endDateInputElement}
                        />
                      </div>
                    </div>

                    <div className="space-y-2">
                      <label
                        className="text-base font-medium text-gray-500"
                        htmlFor=""
                      >
                        Collected
                      </label>
                      <div className="rounded-xl w-full py-4 px-5 flex gap-2 border border-black">
                        <select
                          name="workerIds"
                          className="w-full outline-none"
                          value={selectedCollected}
                          onChange={(e) => setSelectedCollected(e.target.value)}
                        >
                          <option value="false">No</option>
                          <option value="true">Yes</option>
                        </select>
                      </div>
                    </div>

                    <div className="space-y-2">
                      <label
                        className="text-base font-medium text-gray-500"
                        htmlFor=""
                      >
                        Collection Date
                      </label>
                      <div className="rounded-xl py-4 px-5 flex gap-2 border border-black">
                        <input
                          type="date"
                          className="w-full outline-none bg-white"
                          name="collectionDate"
                          defaultValue={
                            jobDetails.data.data.collectionDate
                              ? new Date(jobDetails.data.data.collectionDate)
                                  .toISOString()
                                  .split("T")[0]
                              : ""
                          }
                          ref={collectionDateInputElement}
                        />
                      </div>
                    </div>

                    <h1 className="text-lg text-center font-bold">
                      Bonus Sharing
                    </h1>
                    <div className="flex justify-between mb-2">
                      <b>Foreman share</b>
                      <b className="text-green-500">{foremanShare} %</b>
                    </div>

                    {userDetails?.data?.data?.workers.map((worker) => (
                      <div
                        key={worker._id}
                        className="flex justify-between items-center mb-2"
                      >
                        <b>{worker.name}</b>
                        <div>
                          <input
                            type="number"
                            onWheel={(e) => e.target.blur()}
                            min="0"
                            max="100"
                            value={workerShares[worker._id] || 0}
                            onChange={(e) =>
                              handleWorkerShareChange(
                                worker._id,
                                parseInt(e.target.value) || 0
                              )
                            }
                            className="border-gray-300 border rounded-lg px-2 py-1 w-14"
                          />{" "}
                          %
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="pt-4">
                  <button className="px-3 py-4 w-full text-white font-medium rounded-xl bg-[#59c3c2] ">
                    {isUpdateJobLoading ? "...Is Saving" : "Save"}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </>
      )}
    </>
  );
}
